import { ArrowBack, Business, Delete, Edit, Add, Settings, Close, InfoOutlined } from "@material-ui/icons";
import Switch from "@material-ui/core/Switch";
import Tooltip from "@material-ui/core/Tooltip";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import fetchClient from "../../api/fetchClient";
import Loader from "../../components/Loader";
import message from "../../helper/message";
import AplicacaoPage from "./AplicacaoPage";
import {
  Page,
  ModernHeader,
  HeaderLeft,
  HeaderContent,
  HeaderTitle,
  HeaderSubtitle,
  HeaderActions,
  HeaderActionButton,
  BackButton,
  ContentCard,
  getTheme,
  spin,
} from "../../components/common";

const PRIMARY = "#667eea";
const DANGER = "#e74c3c";

function InfoTip({ text }) {
    return (
        <Tooltip title={text} arrow placement="right" enterDelay={200}>
            <InfoTipIcon />
        </Tooltip>
    );
}

const DEFAULT_WORKER_CONFIG = {
    enableLogs: true,
    enableNotifications: true,
    enableInvoiceLockCheck: true,
    pauseMessagesOnLock: true,
    pauseVisibilitySeconds: 300,
    minLockSecondsThreshold: 30,
    enableDynamicParallelism: false,
    minParallelCalls: 2,
    maxParallelCalls: 7,
    maxRetries: 3,
    parallelismIncreaseStep: 1,
    parallelismIncreaseCooldown: 5,
    parallelismDecreaseStepLight: 1,
    parallelismDecreaseStepModerate: 2,
    parallelismDecreaseStepSevere: 3,
    parentGroup: "",
    // Indicadores de Saúde — thresholds configuráveis
    healthThresholds: {
        errorRateWarning:    5,   // % — acima disso: atenção
        errorRateCritical:   15,  // % — acima disso: crítico
        retryRateWarning:    3,   // %
        retryRateCritical:   10,  // %
        throughputWarning:   80,  // % — abaixo disso: atenção
        throughputCritical:  95,  // % — acima disso: saudável (invertido)
        lockRateWarning:     20,  // % de ciclos com lock
        lockRateCritical:    50,  // %
        parallelChgWarning:  10,  // nº de ajustes
        parallelChgCritical: 50,  // nº de ajustes
    },
};

function AplicacaoComerzziaGroups() {
    const history = useHistory();
    const theme = getTheme('comerzzia');
    const [empresas, setEmpresas] = useState([]);
    const [isViewing, setIsViewing] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [newGroupName, setNewGroupName] = useState("");
    const [editandoGrupo, setEditandoGrupo] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [workerConfig, setWorkerConfig] = useState(DEFAULT_WORKER_CONFIG);
    const [savingConfig, setSavingConfig] = useState(false);
    const [loadingConfig, setLoadingConfig] = useState(false);

    useEffect(() => {
        getAllAplicacoes();
    }, [isViewing]);

    const getAllAplicacoes = async () => {
        try {
            setIsLoading(true);
            const { data } = await fetchClient.get("/comerzzia/getAllGroups");
            setEmpresas(data);
        } catch (error) {
            message.error(error.response?.data?.message || "Erro ao buscar grupos");
        } finally {
            setIsLoading(false);
        }
    };

    const openCreateModal = () => {
        setEditandoGrupo(null);
        setNewGroupName("");
        setWorkerConfig(DEFAULT_WORKER_CONFIG);
        setShowModal(true);
    };

    const openEditModal = async (grupo) => {
        setEditandoGrupo(grupo);
        setNewGroupName(grupo.id);
        setWorkerConfig(DEFAULT_WORKER_CONFIG);
        setLoadingConfig(true);
        setShowModal(true);
        try {
            const { data } = await fetchClient.get(`/worker/${grupo.id}/config`);
            setWorkerConfig({ ...DEFAULT_WORKER_CONFIG, ...(data.workerConfig || data) });
        } catch {
            // mantém defaults se não houver config salva
        } finally {
            setLoadingConfig(false);
        }
    };

    const closeModal = () => {
        setShowModal(false);
        setEditandoGrupo(null);
        setNewGroupName("");
        setWorkerConfig(DEFAULT_WORKER_CONFIG);
    };

    const setConfigField = (key, val) => setWorkerConfig((p) => ({ ...p, [key]: val }));

    const handleSave = async () => {
        try {
            if (!newGroupName.trim()) {
                return message.error("Informe o nome do grupo");
            }
            setSavingConfig(true);
            if (editandoGrupo) {
                await fetchClient.put(`/worker/${editandoGrupo.id}/config`, { workerConfig });
                message.sucess("Configurações salvas com sucesso!");
            } else {
                await fetchClient.post("/comerzzia/groups", { id: newGroupName.trim() });
                await fetchClient.put(`/worker/${newGroupName.trim()}/config`, { workerConfig });
                message.sucess("Grupo criado com sucesso!");
            }
            closeModal();
            getAllAplicacoes();
        } catch (error) {
            message.error(error.response?.data?.message || "Erro ao salvar grupo");
        } finally {
            setSavingConfig(false);
        }
    };

    const onDelete = async (id) => {
        const confirmar = window.confirm("Deseja realmente excluir este grupo?");
        if (!confirmar) return;
        try {
            setDeletingId(id);
            await fetchClient.delete(`/comerzzia/groups/${id}`);
            message.sucess("Grupo deletado com sucesso");
            getAllAplicacoes();
        } catch (error) {
            message.error(error.response?.data?.message || "Erro ao deletar grupo");
        } finally {
            setDeletingId(null);
        }
    };

    if (isLoading) return <Loader />;

    return (
        <Page>
            {/* ── MODAL ───────────────────────────────────────────── */}
            {showModal && (
                <ModalOverlay onClick={(e) => e.target === e.currentTarget && closeModal()}>
                    <ModalBox wide={!!editandoGrupo}>
                        {/* Header */}
                        <ModalHeader editing={!!editandoGrupo}>
                            <ModalHeaderIcon>
                                {editandoGrupo ? <Settings /> : <Add />}
                            </ModalHeaderIcon>
                            <ModalHeaderText>
                                <ModalTitle>
                                    {editandoGrupo ? `Configurações — ${editandoGrupo.id}` : "Novo Grupo"}
                                </ModalTitle>
                                <ModalSubtitle>
                                    {editandoGrupo
                                        ? "Ajuste as configurações do worker para este grupo"
                                        : "Crie um novo grupo de empresas Comerzzia"}
                                </ModalSubtitle>
                            </ModalHeaderText>
                            <CloseBtn onClick={closeModal}><Close style={{ fontSize: 20 }} /></CloseBtn>
                        </ModalHeader>

                        {/* Body */}
                        <ModalBody>
                            {/* ID do grupo */}
                            <FieldBlock>
                                <FieldLabel>ID do Grupo</FieldLabel>
                                <FieldInput
                                    type="text"
                                    placeholder="Ex: FROSTY, GRUPO_A..."
                                    value={newGroupName}
                                    disabled={!!editandoGrupo?.id}
                                    onChange={(e) => setNewGroupName(e.target.value.toUpperCase())}
                                />
                                {!editandoGrupo && (
                                    <FieldHint>Identificador único. Letras maiúsculas e underline recomendados.</FieldHint>
                                )}
                            </FieldBlock>

                            {/* Grupo Principal */}
                            <FieldBlock>
                                <FieldLabel>Grupo Principal</FieldLabel>
                                <FieldInput
                                    type="text"
                                    placeholder="Ex: DEV_PRINCIPAL, PRODUCAO..."
                                    value={workerConfig.parentGroup}
                                    onChange={(e) => setConfigField("parentGroup", e.target.value.toUpperCase())}
                                />
                                <FieldHint>Agrupa este worker no painel consolidado do dashboard.</FieldHint>
                            </FieldBlock>

                            {/* Worker Config (apenas na edição) */}
                            {editandoGrupo && (
                                loadingConfig ? (
                                    <ConfigLoading>
                                        <LoaderSpinner />
                                        Carregando configurações...
                                    </ConfigLoading>
                                ) : (
                                    <>
                                        {/* Seção Geral */}
                                        <ConfigCard>
                                            <ConfigCardHeader color="#27ae60">
                                                <ConfigCardTitle>Geral</ConfigCardTitle>
                                            </ConfigCardHeader>
                                            <ConfigCardBody>
                                                <SwitchRow>
                                                    <SwitchLabel>
                                                        <SwitchLabelTitle>Habilitar logs <InfoTip text="Habilita ou desabilita os logs de operação do worker. Quando false, apenas erros críticos são exibidos no console. Útil para reduzir ruído em produção quando o worker está estável." /></SwitchLabelTitle>
                                                        <SwitchLabelSub>Registra eventos de processamento no CloudWatch</SwitchLabelSub>
                                                    </SwitchLabel>
                                                    <Switch
                                                        checked={workerConfig.enableLogs}
                                                        onChange={(e) => setConfigField("enableLogs", e.target.checked)}
                                                        color="primary"
                                                        size="small"
                                                    />
                                                </SwitchRow>
                                                <SwitchRow>
                                                    <SwitchLabel>
                                                        <SwitchLabelTitle>Enviar notificações <InfoTip text="Quando ativo, o worker envia notificações (ex: erros críticos, lock prolongado, falhas de retry) para o canal configurado. Desative apenas temporariamente durante manutenções para evitar alertas falsos." /></SwitchLabelTitle>
                                                        <SwitchLabelSub>Alertas de erros e eventos críticos do worker</SwitchLabelSub>
                                                    </SwitchLabel>
                                                    <Switch
                                                        checked={workerConfig.enableNotifications}
                                                        onChange={(e) => setConfigField("enableNotifications", e.target.checked)}
                                                        color="primary"
                                                        size="small"
                                                    />
                                                </SwitchRow>
                                            </ConfigCardBody>
                                        </ConfigCard>

                                        {/* Seção Locks */}
                                        <ConfigCard>
                                            <ConfigCardHeader color="#e74c3c">
                                                <ConfigCardTitle>Verificação de Locks</ConfigCardTitle>
                                            </ConfigCardHeader>
                                            <ConfigCardBody>
                                                <SwitchRow>
                                                    <SwitchLabel>
                                                        <SwitchLabelTitle>Verificar locks (OINV/OCRD) <InfoTip text="Ativa a consulta de bloqueios nas tabelas do HANA (M_BLOCKED_TRANSACTIONS) a cada ciclo de polling. Detecta locks em tabelas críticas como OINV e OCRD. Necessário para que o paralelismo dinâmico e a pausa de mensagens funcionem corretamente." /></SwitchLabelTitle>
                                                        <SwitchLabelSub>Detecta bloqueios nas tabelas de NF-e</SwitchLabelSub>
                                                    </SwitchLabel>
                                                    <Switch
                                                        checked={workerConfig.enableInvoiceLockCheck}
                                                        onChange={(e) => setConfigField("enableInvoiceLockCheck", e.target.checked)}
                                                        color="primary"
                                                        size="small"
                                                    />
                                                </SwitchRow>
                                                <SwitchRow>
                                                    <SwitchLabel>
                                                        <SwitchLabelTitle>Pausar mensagens em lock <InfoTip text="Quando uma empresa está com lock detectado no HANA, suas mensagens na fila SQS são pausadas temporariamente (via VisibilityTimeout). Isso evita que o worker continue tentando processar documentos de uma base bloqueada, reduzindo erros e carga desnecessária no SAP B1." /></SwitchLabelTitle>
                                                        <SwitchLabelSub>Pausa o processamento quando há lock ativo</SwitchLabelSub>
                                                    </SwitchLabel>
                                                    <Switch
                                                        checked={workerConfig.pauseMessagesOnLock}
                                                        onChange={(e) => setConfigField("pauseMessagesOnLock", e.target.checked)}
                                                        color="primary"
                                                        size="small"
                                                    />
                                                </SwitchRow>
                                                <ConfigDivider />
                                                <NumericGrid>
                                                    <NumericField>
                                                        <NumericLabel>Pausa (seg) <InfoTip text="Tempo em segundos que as mensagens de uma empresa com lock ficam invisíveis na fila (não serão consumidas). Após este período, as mensagens voltam a aparecer e o worker tenta processá-las novamente. Padrão: 300s (5 minutos)." /></NumericLabel>
                                                        <NumberInput
                                                            type="number"
                                                            min={60} max={600} step={30}
                                                            value={workerConfig.pauseVisibilitySeconds}
                                                            onChange={(e) => setConfigField("pauseVisibilitySeconds", Number(e.target.value))}
                                                        />
                                                        <NumericHint>60 – 600s</NumericHint>
                                                    </NumericField>
                                                    <NumericField>
                                                        <NumericLabel>Threshold mínimo (seg) <InfoTip text="Tempo mínimo em segundos que um lock precisa ter para ser considerado problemático. Locks mais curtos são ignorados. Valores menores (ex: 7s) tornam o sistema mais sensível a contenções rápidas; valores maiores (ex: 30s) evitam reações a locks transitórios normais do SAP B1." /></NumericLabel>
                                                        <NumberInput
                                                            type="number"
                                                            min={10} max={120} step={5}
                                                            value={workerConfig.minLockSecondsThreshold}
                                                            onChange={(e) => setConfigField("minLockSecondsThreshold", Number(e.target.value))}
                                                        />
                                                        <NumericHint>10 – 120s</NumericHint>
                                                    </NumericField>
                                                </NumericGrid>
                                            </ConfigCardBody>
                                        </ConfigCard>

                                        {/* Seção Paralelismo */}
                                        <ConfigCard>
                                            <ConfigCardHeader color="#667eea">
                                                <ConfigCardTitle>Controle de Paralelismo</ConfigCardTitle>
                                            </ConfigCardHeader>
                                            <ConfigCardBody>
                                                <SwitchRow>
                                                    <SwitchLabel>
                                                        <SwitchLabelTitle>Paralelismo dinâmico <InfoTip text="Ativa o controle adaptativo de paralelismo. Quando habilitado, o sistema aumenta ou reduz automaticamente o número de chamadas paralelas com base na detecção de locks no HANA e erros 504 do SAP B1 Service Layer. O paralelismo começa no máximo e é reduzido progressivamente em caso de contenção." /></SwitchLabelTitle>
                                                        <SwitchLabelSub>Ajusta o número de threads automaticamente</SwitchLabelSub>
                                                    </SwitchLabel>
                                                    <Switch
                                                        checked={workerConfig.enableDynamicParallelism}
                                                        onChange={(e) => setConfigField("enableDynamicParallelism", e.target.checked)}
                                                        color="primary"
                                                        size="small"
                                                    />
                                                </SwitchRow>
                                                <ConfigDivider />
                                                <NumericGrid>
                                                    <NumericField>
                                                        <NumericLabel>Mín. threads <InfoTip text="Piso mínimo de paralelismo quando o controle dinâmico está reduzindo. O worker nunca processa menos que este valor simultaneamente, mesmo sob lock severo. Padrão: 2 (env)." /></NumericLabel>
                                                        <NumberInput
                                                            type="number"
                                                            min={1} max={20} step={1}
                                                            value={workerConfig.minParallelCalls}
                                                            onChange={(e) => setConfigField("minParallelCalls", Number(e.target.value))}
                                                        />
                                                        <NumericHint>1 – 20</NumericHint>
                                                    </NumericField>
                                                    <NumericField>
                                                        <NumericLabel>Máx. threads <InfoTip text="Número máximo de mensagens processadas simultaneamente por ciclo. Valores altos aumentam a throughput mas elevam o risco de conflitos no SAP B1 (erros 504 e locks no HANA). Recomendado: entre 5 e 15. Padrão: 7 (env)." /></NumericLabel>
                                                        <NumberInput
                                                            type="number"
                                                            min={1} max={50} step={1}
                                                            value={workerConfig.maxParallelCalls}
                                                            onChange={(e) => setConfigField("maxParallelCalls", Number(e.target.value))}
                                                        />
                                                        <NumericHint>1 – 50</NumericHint>
                                                    </NumericField>
                                                </NumericGrid>
                                                <ConfigDivider />
                                                <NumericGrid>
                                                    <NumericField>
                                                        <NumericLabel>Incremento por aumento <InfoTip text="Quantidade de chamadas adicionadas ao paralelismo a cada aumento. Valores maiores fazem a recuperação ser mais rápida após um período estável." /></NumericLabel>
                                                        <NumberInput
                                                            type="number"
                                                            min={1} max={10} step={1}
                                                            value={workerConfig.parallelismIncreaseStep}
                                                            onChange={(e) => setConfigField("parallelismIncreaseStep", Number(e.target.value))}
                                                        />
                                                        <NumericHint>1 – 10</NumericHint>
                                                    </NumericField>
                                                    <NumericField>
                                                        <NumericLabel>Ciclos para aumentar <InfoTip text="Número de ciclos consecutivos sem erros ou locks necessários antes de aumentar o paralelismo. Valores menores = recuperação mais rápida; valores maiores = mais conservador." /></NumericLabel>
                                                        <NumberInput
                                                            type="number"
                                                            min={1} max={30} step={1}
                                                            value={workerConfig.parallelismIncreaseCooldown}
                                                            onChange={(e) => setConfigField("parallelismIncreaseCooldown", Number(e.target.value))}
                                                        />
                                                        <NumericHint>1 – 30 ciclos</NumericHint>
                                                    </NumericField>
                                                </NumericGrid>
                                                <ConfigDivider />
                                                <NumericGrid>
                                                    <NumericField>
                                                        <NumericLabel>Redução por lock leve <InfoTip text="Quantidade removida do paralelismo quando detectado um lock com duração menor que 10 segundos no HANA." /></NumericLabel>
                                                        <NumberInput
                                                            type="number"
                                                            min={1} max={10} step={1}
                                                            value={workerConfig.parallelismDecreaseStepLight}
                                                            onChange={(e) => setConfigField("parallelismDecreaseStepLight", Number(e.target.value))}
                                                        />
                                                        <NumericHint>Lock &lt; 10s</NumericHint>
                                                    </NumericField>
                                                    <NumericField>
                                                        <NumericLabel>Redução por lock moderado <InfoTip text="Quantidade removida do paralelismo quando detectado um lock com duração entre 10 e 30 segundos no HANA." /></NumericLabel>
                                                        <NumberInput
                                                            type="number"
                                                            min={1} max={10} step={1}
                                                            value={workerConfig.parallelismDecreaseStepModerate}
                                                            onChange={(e) => setConfigField("parallelismDecreaseStepModerate", Number(e.target.value))}
                                                        />
                                                        <NumericHint>Lock 10–30s</NumericHint>
                                                    </NumericField>
                                                    <NumericField>
                                                        <NumericLabel>Redução por lock severo <InfoTip text="Quantidade removida do paralelismo quando detectado um lock acima de 30 segundos no HANA, ou quando ocorre erro 504 do SAP B1 Service Layer." /></NumericLabel>
                                                        <NumberInput
                                                            type="number"
                                                            min={1} max={10} step={1}
                                                            value={workerConfig.parallelismDecreaseStepSevere}
                                                            onChange={(e) => setConfigField("parallelismDecreaseStepSevere", Number(e.target.value))}
                                                        />
                                                        <NumericHint>Lock &gt; 30s / 504</NumericHint>
                                                    </NumericField>
                                                </NumericGrid>
                                            </ConfigCardBody>
                                        </ConfigCard>

                                        {/* Seção Retentativas */}
                                        <ConfigCard>
                                            <ConfigCardHeader color="#f39c12">
                                                <ConfigCardTitle>Retentativas</ConfigCardTitle>
                                            </ConfigCardHeader>
                                            <ConfigCardBody>
                                                <NumericGrid style={{ paddingTop: 0 }}>
                                                    <NumericField>
                                                        <NumericLabel>Máx. retries <InfoTip text="Número máximo de tentativas de reprocessamento para mensagens com erro 504 (timeout do SAP B1 Service Layer). 0 significa sem limite — a mensagem fica na fila indefinidamente até ser processada com sucesso. Valores como 3 ou 5 movem a mensagem para dead-letter após N falhas." /></NumericLabel>
                                                        <NumberInput
                                                            type="number"
                                                            min={0} max={20} step={1}
                                                            value={workerConfig.maxRetries}
                                                            onChange={(e) => setConfigField("maxRetries", Number(e.target.value))}
                                                        />
                                                        <NumericHint>0 – 20</NumericHint>
                                                    </NumericField>
                                                </NumericGrid>
                                            </ConfigCardBody>
                                        </ConfigCard>

                                        {/* Seção Indicadores de Saúde */}
                                        <ConfigCard>
                                            <ConfigCardHeader color="#8e44ad">
                                                <ConfigCardTitle>Indicadores de Saúde — Thresholds</ConfigCardTitle>
                                            </ConfigCardHeader>
                                            <ConfigCardBody>
                                                <NumericGrid style={{ paddingTop: 0 }}>
                                                    <NumericField>
                                                        <NumericLabel>Taxa de Erro — Atenção (%) <InfoTip text="Percentual de falhas acima do qual o indicador 'Taxa de Erro Total' entra em atenção. Padrão: 5%." /></NumericLabel>
                                                        <NumberInput type="number" min={1} max={99} step={1}
                                                            value={workerConfig.healthThresholds?.errorRateWarning ?? 5}
                                                            onChange={(e) => setConfigField("healthThresholds", { ...workerConfig.healthThresholds, errorRateWarning: Number(e.target.value) })}
                                                        />
                                                        <NumericHint>Padrão: 5%</NumericHint>
                                                    </NumericField>
                                                    <NumericField>
                                                        <NumericLabel>Taxa de Erro — Crítico (%) <InfoTip text="Percentual de falhas acima do qual o indicador entra em estado crítico. Padrão: 15%." /></NumericLabel>
                                                        <NumberInput type="number" min={1} max={100} step={1}
                                                            value={workerConfig.healthThresholds?.errorRateCritical ?? 15}
                                                            onChange={(e) => setConfigField("healthThresholds", { ...workerConfig.healthThresholds, errorRateCritical: Number(e.target.value) })}
                                                        />
                                                        <NumericHint>Padrão: 15%</NumericHint>
                                                    </NumericField>
                                                    <NumericField>
                                                        <NumericLabel>Retry Errors — Atenção (%) <InfoTip text="Percentual de retries acima do qual o indicador entra em atenção. Padrão: 3%." /></NumericLabel>
                                                        <NumberInput type="number" min={1} max={99} step={1}
                                                            value={workerConfig.healthThresholds?.retryRateWarning ?? 3}
                                                            onChange={(e) => setConfigField("healthThresholds", { ...workerConfig.healthThresholds, retryRateWarning: Number(e.target.value) })}
                                                        />
                                                        <NumericHint>Padrão: 3%</NumericHint>
                                                    </NumericField>
                                                    <NumericField>
                                                        <NumericLabel>Retry Errors — Crítico (%) <InfoTip text="Percentual de retries acima do qual o indicador entra em crítico. Padrão: 10%." /></NumericLabel>
                                                        <NumberInput type="number" min={1} max={100} step={1}
                                                            value={workerConfig.healthThresholds?.retryRateCritical ?? 10}
                                                            onChange={(e) => setConfigField("healthThresholds", { ...workerConfig.healthThresholds, retryRateCritical: Number(e.target.value) })}
                                                        />
                                                        <NumericHint>Padrão: 10%</NumericHint>
                                                    </NumericField>
                                                    <NumericField>
                                                        <NumericLabel>Consumo da Fila — Atenção (%) <InfoTip text="Percentual de throughput abaixo do qual o indicador entra em atenção. Padrão: 80%." /></NumericLabel>
                                                        <NumberInput type="number" min={1} max={99} step={1}
                                                            value={workerConfig.healthThresholds?.throughputWarning ?? 80}
                                                            onChange={(e) => setConfigField("healthThresholds", { ...workerConfig.healthThresholds, throughputWarning: Number(e.target.value) })}
                                                        />
                                                        <NumericHint>Padrão: 80%</NumericHint>
                                                    </NumericField>
                                                    <NumericField>
                                                        <NumericLabel>Consumo da Fila — Saudável (%) <InfoTip text="Percentual de throughput acima do qual o indicador é saudável. Padrão: 95%." /></NumericLabel>
                                                        <NumberInput type="number" min={1} max={100} step={1}
                                                            value={workerConfig.healthThresholds?.throughputCritical ?? 95}
                                                            onChange={(e) => setConfigField("healthThresholds", { ...workerConfig.healthThresholds, throughputCritical: Number(e.target.value) })}
                                                        />
                                                        <NumericHint>Padrão: 95%</NumericHint>
                                                    </NumericField>
                                                    <NumericField>
                                                        <NumericLabel>Pressão de Lock — Atenção (%) <InfoTip text="Percentual de ciclos com lock acima do qual o indicador entra em atenção. Padrão: 20%." /></NumericLabel>
                                                        <NumberInput type="number" min={1} max={99} step={1}
                                                            value={workerConfig.healthThresholds?.lockRateWarning ?? 20}
                                                            onChange={(e) => setConfigField("healthThresholds", { ...workerConfig.healthThresholds, lockRateWarning: Number(e.target.value) })}
                                                        />
                                                        <NumericHint>Padrão: 20%</NumericHint>
                                                    </NumericField>
                                                    <NumericField>
                                                        <NumericLabel>Pressão de Lock — Crítico (%) <InfoTip text="Percentual de ciclos com lock acima do qual o indicador entra em crítico. Padrão: 50%." /></NumericLabel>
                                                        <NumberInput type="number" min={1} max={100} step={1}
                                                            value={workerConfig.healthThresholds?.lockRateCritical ?? 50}
                                                            onChange={(e) => setConfigField("healthThresholds", { ...workerConfig.healthThresholds, lockRateCritical: Number(e.target.value) })}
                                                        />
                                                        <NumericHint>Padrão: 50%</NumericHint>
                                                    </NumericField>
                                                    <NumericField>
                                                        <NumericLabel>Estab. Paralelismo — Atenção (ajustes) <InfoTip text="Número de ajustes de paralelismo acima do qual o indicador entra em atenção. Padrão: 10." /></NumericLabel>
                                                        <NumberInput type="number" min={1} max={999} step={1}
                                                            value={workerConfig.healthThresholds?.parallelChgWarning ?? 10}
                                                            onChange={(e) => setConfigField("healthThresholds", { ...workerConfig.healthThresholds, parallelChgWarning: Number(e.target.value) })}
                                                        />
                                                        <NumericHint>Padrão: 10</NumericHint>
                                                    </NumericField>
                                                    <NumericField>
                                                        <NumericLabel>Estab. Paralelismo — Crítico (ajustes) <InfoTip text="Número de ajustes de paralelismo acima do qual o indicador entra em crítico. Padrão: 50." /></NumericLabel>
                                                        <NumberInput type="number" min={1} max={999} step={1}
                                                            value={workerConfig.healthThresholds?.parallelChgCritical ?? 50}
                                                            onChange={(e) => setConfigField("healthThresholds", { ...workerConfig.healthThresholds, parallelChgCritical: Number(e.target.value) })}
                                                        />
                                                        <NumericHint>Padrão: 50</NumericHint>
                                                    </NumericField>
                                                </NumericGrid>
                                            </ConfigCardBody>
                                        </ConfigCard>

                                        <ConfigNotice>
                                            ⏱ Alterações entram em vigor em até 1 minuto sem necessidade de restart do serviço.
                                        </ConfigNotice>
                                    </>
                                )
                            )}
                        </ModalBody>

                        {/* Footer */}
                        <ModalFooter>
                            <CancelBtn onClick={closeModal}>Cancelar</CancelBtn>
                            <SaveBtn onClick={handleSave} disabled={savingConfig}>
                                {savingConfig ? <><LoaderSpinnerSmall />Salvando...</> : "Salvar"}
                            </SaveBtn>
                        </ModalFooter>
                    </ModalBox>
                </ModalOverlay>
            )}

            {/* ── HEADER ──────────────────────────────────────────── */}
            <ModernHeader>
                <HeaderLeft>
                    <BackButton
                        color={theme.primary}
                        onClick={() => isViewing ? setIsViewing(false) : history.goBack()}
                    >
                        <ArrowBack />
                    </BackButton>
                    <HeaderContent>
                        <HeaderTitle>
                            <Business style={{ marginRight: "12px", fontSize: "32px" }} />
                            Grupos Comerzzia
                        </HeaderTitle>
                        <HeaderSubtitle>
                            {empresas.length} grupo{empresas.length !== 1 ? "s" : ""} cadastrado{empresas.length !== 1 ? "s" : ""}
                        </HeaderSubtitle>
                    </HeaderContent>
                </HeaderLeft>
                <HeaderActions>
                    <HeaderActionButton gradient={theme.gradient} onClick={openCreateModal}>
                        <Add style={{ marginRight: '8px', fontSize: '20px' }} />
                        Novo grupo
                    </HeaderActionButton>
                </HeaderActions>
            </ModernHeader>

            {/* ── CONTEÚDO ────────────────────────────────────────── */}
            <ContentCard>
                {isViewing ? (
                    <AplicacaoPage data={isViewing} isViewing={setIsViewing} />
                ) : empresas.length === 0 ? (
                    <EmptyState>
                        <Business style={{ fontSize: 56, color: "#cbd5e1" }} />
                        <EmptyTitle>Nenhum grupo cadastrado</EmptyTitle>
                        <EmptyText>Crie o primeiro grupo para começar a organizar as empresas Comerzzia.</EmptyText>
                        <EmptyBtn onClick={openCreateModal}>
                            <Add style={{ fontSize: 18, marginRight: 6 }} />
                            Criar primeiro grupo
                        </EmptyBtn>
                    </EmptyState>
                ) : (
                    <GroupGrid>
                        {empresas.map((grupo, idx) => (
                            <GroupCard key={grupo.id} index={idx}>
                                <GroupCardTop>
                                    <GroupIcon>{grupo.id.charAt(0)}</GroupIcon>
                                    <GroupInfo>
                                        <GroupName>{grupo.id}</GroupName>
                                        <GroupMeta>{grupo.parentGroup || "Sem grupo principal"}</GroupMeta>
                                    </GroupInfo>
                                </GroupCardTop>
                                <GroupCardActions>
                                    <EditBtn onClick={() => openEditModal(grupo)}>
                                        <Settings style={{ fontSize: 16, marginRight: 5 }} />
                                        Configurar
                                    </EditBtn>
                                    <DeleteBtn
                                        onClick={() => onDelete(grupo.id)}
                                        disabled={deletingId === grupo.id}
                                    >
                                        {deletingId === grupo.id
                                            ? <LoaderSpinnerSmall />
                                            : <Delete style={{ fontSize: 16 }} />
                                        }
                                    </DeleteBtn>
                                </GroupCardActions>
                            </GroupCard>
                        ))}
                    </GroupGrid>
                )}
            </ContentCard>
        </Page>
    );
}

export default AplicacaoComerzziaGroups;

// ── Animations ────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-16px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

// ── Info tooltip icon ─────────────────────────────────────────
const InfoTipIcon = styled(InfoOutlined)`
    font-size: 14px !important;
    color: #94a3b8;
    cursor: help;
    flex-shrink: 0;
    transition: color 0.15s;
    &:hover { color: #667eea; }
`;

// ── Modal ─────────────────────────────────────────────────────
const ModalOverlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.45);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
    padding: 24px;
    backdrop-filter: blur(2px);
`;

const ModalBox = styled.div`
    background: white;
    border-radius: 20px;
    width: 100%;
    max-width: ${(p) => (p.wide ? "580px" : "420px")};
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 24px 60px rgba(15, 23, 42, 0.22);
    animation: ${slideDown} 0.25s ease;
    overflow: hidden;
`;

const ModalHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 24px 24px 20px;
    background: ${(p) => p.editing
        ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
        : "linear-gradient(135deg, #27ae60 0%, #16a34a 100%)"};
    color: white;
    position: relative;
`;

const ModalHeaderIcon = styled.div`
    width: 42px;
    height: 42px;
    border-radius: 12px;
    background: rgba(255,255,255,0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    svg { font-size: 22px; }
`;

const ModalHeaderText = styled.div`
    flex: 1;
`;

const ModalTitle = styled.h3`
    margin: 0 0 3px 0;
    font-size: 17px;
    font-weight: 700;
    font-family: Mulish, sans-serif;
`;

const ModalSubtitle = styled.p`
    margin: 0;
    font-size: 13px;
    opacity: 0.85;
    font-family: Mulish, sans-serif;
`;

const CloseBtn = styled.button`
    width: 34px;
    height: 34px;
    border-radius: 8px;
    border: none;
    background: rgba(255,255,255,0.15);
    color: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s;
    flex-shrink: 0;
    &:hover { background: rgba(255,255,255,0.28); }
`;

const ModalBody = styled.div`
    padding: 24px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 16px;
    flex: 1;
`;

const ModalFooter = styled.div`
    padding: 16px 24px;
    border-top: 1px solid #f0f0f0;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    background: #fafafa;
`;

// ── Form fields ───────────────────────────────────────────────
const FieldBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const FieldLabel = styled.label`
    font-size: 12px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-family: Mulish, sans-serif;
`;

const FieldInput = styled.input`
    padding: 11px 14px;
    font-size: 15px;
    font-weight: 600;
    border: 2px solid ${(p) => p.disabled ? "#e5e7eb" : "#e2e8f0"};
    border-radius: 10px;
    font-family: Mulish, sans-serif;
    color: ${(p) => p.disabled ? "#94a3b8" : "#2c3e50"};
    background: ${(p) => p.disabled ? "#f9fafb" : "white"};
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;
    width: 100%;
    box-sizing: border-box;
    letter-spacing: 0.5px;

    &:focus:not(:disabled) {
        border-color: #667eea;
        box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.12);
    }
`;

const FieldHint = styled.span`
    font-size: 12px;
    color: #94a3b8;
    font-family: Mulish, sans-serif;
`;

// ── Config cards ──────────────────────────────────────────────
const ConfigLoading = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    color: #64748b;
    font-size: 14px;
    font-family: Mulish, sans-serif;
    padding: 20px 0;
`;

const ConfigCard = styled.div`
    border: 1px solid #e8e8e8;
    border-radius: 14px;
    box-shadow: 0 1px 4px rgba(0,0,0,0.04);
`;

const ConfigCardHeader = styled.div`
    padding: 12px 16px;
    background: ${(p) => `${p.color}0f`};
    border-bottom: 1px solid ${(p) => `${p.color}20`};
    border-radius: 14px 14px 0 0;
`;

const ConfigCardTitle = styled.h4`
    margin: 0;
    font-size: 13px;
    font-weight: 700;
    color: #2c3e50;
    font-family: Mulish, sans-serif;
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

const ConfigCardBody = styled.div`
    padding: 12px 16px;
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const SwitchRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 0;
    border-bottom: 1px solid #f5f5f5;
    &:last-of-type { border-bottom: none; }
`;

const SwitchLabel = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
    padding-right: 12px;
`;

const SwitchLabelTitle = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: #2c3e50;
    font-family: Mulish, sans-serif;
    display: flex;
    align-items: center;
    gap: 5px;
`;

const SwitchLabelSub = styled.span`
    font-size: 11px;
    color: #94a3b8;
    font-family: Mulish, sans-serif;
`;

const ConfigDivider = styled.div`
    height: 1px;
    background: #f0f0f0;
    margin: 6px 0 4px;
`;

const NumericGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    padding-top: 10px;
`;

const NumericField = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const NumericLabel = styled.label`
    font-size: 11px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-family: Mulish, sans-serif;
    display: flex;
    align-items: center;
    gap: 4px;
`;

const NumberInput = styled.input`
    padding: 8px 10px;
    border: 1.5px solid #e2e8f0;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    font-family: Mulish, sans-serif;
    text-align: center;
    color: #2c3e50;
    width: 100%;
    box-sizing: border-box;
    transition: border-color 0.2s;
    &:focus { outline: none; border-color: #667eea; box-shadow: 0 0 0 3px rgba(102,126,234,0.1); }
`;

const NumericHint = styled.span`
    font-size: 10px;
    color: #94a3b8;
    text-align: center;
    font-family: Mulish, sans-serif;
`;

const ConfigNotice = styled.p`
    font-size: 12px;
    color: #64748b;
    margin: 0;
    padding: 10px 14px;
    background: #f8f9fa;
    border-radius: 10px;
    border-left: 3px solid #f39c12;
    font-family: Mulish, sans-serif;
    font-style: italic;
`;

// ── Modal buttons ─────────────────────────────────────────────
const CancelBtn = styled.button`
    padding: 10px 20px;
    border: 2px solid #e5e7eb;
    border-radius: 10px;
    background: white;
    color: #64748b;
    font-family: Mulish, sans-serif;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    &:hover { border-color: #94a3b8; color: #2c3e50; }
`;

const SaveBtn = styled.button`
    padding: 10px 24px;
    border: none;
    border-radius: 10px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    font-family: Mulish, sans-serif;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: all 0.2s ease;
    box-shadow: 0 4px 12px rgba(102,126,234,0.35);
    &:hover:not(:disabled) { opacity: 0.88; transform: translateY(-1px); }
    &:disabled { opacity: 0.55; cursor: not-allowed; box-shadow: none; transform: none; }
`;

// ── Group grid / cards ────────────────────────────────────────
const GroupGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 20px;
`;

const GroupCard = styled.div`
    background: white;
    border: 1px solid #e8e8e8;
    border-radius: 16px;
    padding: 20px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
    display: flex;
    flex-direction: column;
    gap: 16px;
    transition: all 0.25s ease;
    animation: ${fadeUp} 0.35s ease both;
    animation-delay: ${(p) => p.index * 0.05}s;

    &:hover {
        box-shadow: 0 8px 24px rgba(102,126,234,0.13);
        border-color: #c7d2fe;
        transform: translateY(-3px);
    }
`;

const GroupCardTop = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
`;

const GroupIcon = styled.div`
    width: 48px;
    height: 48px;
    border-radius: 14px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    font-size: 20px;
    font-weight: 800;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: Mulish, sans-serif;
    flex-shrink: 0;
    box-shadow: 0 4px 12px rgba(102,126,234,0.35);
`;

const GroupInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 3px;
    flex: 1;
    min-width: 0;
`;

const GroupName = styled.h3`
    margin: 0;
    font-size: 16px;
    font-weight: 800;
    color: #1e293b;
    font-family: Mulish, sans-serif;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    letter-spacing: 0.3px;
`;

const GroupMeta = styled.span`
    font-size: 12px;
    color: #94a3b8;
    font-family: Mulish, sans-serif;
`;

const GroupCardActions = styled.div`
    display: flex;
    gap: 8px;
    border-top: 1px solid #f0f0f0;
    padding-top: 14px;
`;

const EditBtn = styled.button`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 8px 12px;
    border: none;
    border-radius: 9px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    font-family: Mulish, sans-serif;
    transition: all 0.2s ease;
    box-shadow: 0 2px 8px rgba(102,126,234,0.3);
    &:hover { opacity: 0.88; transform: translateY(-1px); }
`;

const DeleteBtn = styled.button`
    width: 36px;
    height: 36px;
    border: 1.5px solid #fecaca;
    border-radius: 9px;
    background: white;
    color: ${DANGER};
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    flex-shrink: 0;
    &:hover:not(:disabled) { background: #fef2f2; border-color: ${DANGER}; transform: translateY(-1px); }
    &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

// ── Empty state ───────────────────────────────────────────────
const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 60px 24px;
    gap: 12px;
    text-align: center;
`;

const EmptyTitle = styled.h3`
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    color: #2c3e50;
    font-family: Mulish, sans-serif;
`;

const EmptyText = styled.p`
    margin: 0;
    font-size: 14px;
    color: #6b7280;
    max-width: 380px;
    font-family: Mulish, sans-serif;
`;

const EmptyBtn = styled.button`
    margin-top: 8px;
    display: flex;
    align-items: center;
    padding: 12px 24px;
    border: none;
    border-radius: 12px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    font-family: Mulish, sans-serif;
    box-shadow: 0 4px 14px rgba(102,126,234,0.35);
    transition: all 0.2s ease;
    &:hover { opacity: 0.88; transform: translateY(-2px); }
`;

// ── Loaders ───────────────────────────────────────────────────
const LoaderSpinner = styled.div`
    width: 18px;
    height: 18px;
    border: 2px solid #e2e8f0;
    border-top: 2px solid #667eea;
    border-radius: 50%;
    animation: ${spin} 0.7s linear infinite;
    flex-shrink: 0;
`;

const LoaderSpinnerSmall = styled.div`
    width: 14px;
    height: 14px;
    border: 2px solid rgba(255,255,255,0.4);
    border-top: 2px solid white;
    border-radius: 50%;
    animation: ${spin} 0.7s linear infinite;
    flex-shrink: 0;
`;
